/**
 * Adapter functions for converting admin panel dashboard components
 * to distro (survey.ts) dashboard components.
 *
 * This is the reverse of distroToAdminPanel.ts.
 */
import type * as survey from '@aidkitorg/types/lib/survey';
import type { SupportedDashboardComponent } from '@aidkitorg/types/lib/admin-panel/dashboard';

/**
 * Convert a single admin panel component to a distro dashboard component.
 */
function convertComponent(component: SupportedDashboardComponent): survey.DashboardComponent | null {
  switch (component.kind) {
    case 'Hero Number':
      return component as survey.HeroNumberWithFields;
    case 'Bar Chart':
      return component as survey.BarChartWithFields;
    case 'Pie Chart':
      return component as survey.PieChartWithFields;
    case 'Line Chart':
      return component as survey.LineChart;
    case 'Summary Table':
      return component as survey.SummaryTable;
    case 'Map':
      return component as survey.Map;
    case 'Report': {
      const ref: survey.DashboardReportReference = {
        kind: 'Report Reference',
        reportId: component.reportId,
      };
      return ref;
    }
    case 'Dashboard Explanation':
      return component as survey.DashboardExplanation;
    default:
      return component as unknown as survey.DashboardComponent;
  }
}

/**
 * Convert an array of admin panel components to distro dashboard components.
 * This is the inverse of adaptDashboardComponents in distroToAdminPanel.ts.
 *
 * Dashboard Sections arrive with their children already nested in
 * `.components` (matching the original distro structure), so we recursively
 * convert children in place rather than consuming from the flat list.
 */
export function adminPanelComponentsToDistro(
  components: SupportedDashboardComponent[]
): survey.DashboardComponent[] {
  return components.map(comp => convertComponentRecursive(comp)).filter(Boolean) as survey.DashboardComponent[];
}

function convertComponentRecursive(comp: SupportedDashboardComponent): survey.DashboardComponent | null {
  if ((comp as any).kind === 'Dashboard Section') {
    const section = comp as unknown as survey.DashboardSection;
    const convertedChildren = (section.components || [])
      .map(child => convertComponentRecursive(child as unknown as SupportedDashboardComponent))
      .filter(Boolean) as survey.DashboardComponent[];
    return { ...section, components: convertedChildren } as survey.DashboardComponent;
  }
  return convertComponent(comp);
}
